/**
 * CLI session helpers — exchange a Better Auth session cookie for short-lived
 * ordinary or target-bound JWTs against the auth worker.
 */

import { ApiError } from './lib/api'
import { fetchWithTransientRetry } from './lib/fetch-retry'
import { SESSION_COOKIE } from '../shared/auth-session'

export { SESSION_COOKIE }

/**
 * Exchange a Better Auth session token for a fresh JWT.
 * Returns null if the session is invalid or expired.
 */
async function exchangeSessionToken(
  authUrl: string,
  sessionToken: string,
  path: string,
  action: string,
  body?: string,
): Promise<string | null> {
  let res: Response
  try {
    res = await fetchWithTransientRetry(
      `${authUrl}${path}`,
      () => ({
        method: 'POST',
        headers: {
          Cookie: `${SESSION_COOKIE}=${encodeURIComponent(sessionToken)}`,
          Origin: authUrl,
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        body,
      }),
      { timeoutMs: 15_000 },
    )
  } catch (error) {
    throw new ApiError(
      `Could not reach the auth service at ${authUrl}: ${error instanceof Error ? error.message : String(error)}. Check your connection, then retry.`,
      0,
      'network_error',
      path,
    )
  }
  // This is the only response that proves the long-lived session is invalid.
  if (res.status === 401) return null
  if (!res.ok) {
    throw new ApiError(
      `The auth service could not ${action} (HTTP ${res.status}). Retry without logging in again.`,
      res.status,
      res.status === 429 ? 'rate_limited' : 'auth_service_unavailable',
      path,
    )
  }
  let data: { token?: string | null }
  try {
    data = (await res.json()) as { token?: string | null }
  } catch {
    throw new ApiError(
      'The auth service returned a malformed token response.',
      res.status,
      'invalid_response',
      path,
    )
  }
  if (!data.token) {
    throw new ApiError(
      'The auth service returned no token for a valid session.',
      res.status,
      'invalid_response',
      path,
    )
  }
  return data.token
}

/** Exchange a Better Auth session token for a fresh general platform JWT. */
export function exchangeSession(authUrl: string, sessionToken: string): Promise<string | null> {
  return exchangeSessionToken(authUrl, sessionToken, '/api/auth/token', 'refresh the CLI token')
}

/** Mint an in-memory credential usable only at one exact agent target. */
export function exchangeAgentSession(
  authUrl: string,
  sessionToken: string,
  target: string,
): Promise<string | null> {
  return exchangeSessionToken(
    authUrl,
    sessionToken,
    '/api/auth/agent-token',
    'mint the app-specific agent token',
    JSON.stringify({ target }),
  )
}
